/**
 * Workflow JSON Validator
 *
 * Main orchestrator for all workflow JSON validation.
 * Combines structure, node parameter, and edge validation.
 */

import {
  WorkflowJSON,
  SimplifiedWorkflowJSON,
  WorkflowJSONValidationResult,
  WorkflowJSONValidationError,
  WorkflowJSONValidationWarning,
  detectWorkflowFormat,
} from '../types';
import { validateWorkflowStructure, detectCycles } from './structureValidator';
import { validateAllNodeParameters, validateConditionalBranches } from './nodeParameterValidators';
import { validateEdges, validateMergeNodes } from './edgeValidator';

/**
 * Validate a workflow JSON (either full or simplified format)
 */
export function validateWorkflowJSON(json: unknown): WorkflowJSONValidationResult {
  // Parse JSON if string
  let parsed: unknown;
  if (typeof json === 'string') {
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return {
        isValid: false,
        errors: [
          {
            code: 'INVALID_JSON',
            message: `Invalid JSON syntax: ${(e as Error).message}`,
            suggestion: 'Check for syntax errors like missing commas, quotes, or brackets',
          },
        ],
        warnings: [],
      };
    }
  } else {
    parsed = json;
  }

  // Validate JSON is an object
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {
      isValid: false,
      errors: [
        {
          code: 'INVALID_JSON',
          message: 'Workflow JSON must be an object',
          suggestion: 'Provide a valid JSON object with workflow definition',
        },
      ],
      warnings: [],
    };
  }

  // Detect format
  let format: 'full' | 'simplified';
  try {
    format = detectWorkflowFormat(parsed);
  } catch (e) {
    return {
      isValid: false,
      errors: [
        {
          code: 'INVALID_FORMAT',
          message: (e as Error).message,
          suggestion: 'Provide either "nodes" array (full format) or "steps" array (simplified format)',
        },
      ],
      warnings: [],
    };
  }

  // Validate based on format
  if (format === 'simplified') {
    return validateSimplifiedFormat(parsed as SimplifiedWorkflowJSON);
  } else {
    return validateFullFormat(parsed as WorkflowJSON);
  }
}

/**
 * Validate full format workflow JSON
 */
export function validateFullFormat(workflow: WorkflowJSON): WorkflowJSONValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  // Structure validation
  const structureResult = validateWorkflowStructure(workflow);
  errors.push(...structureResult.errors);
  warnings.push(...structureResult.warnings);

  // If structure has critical errors, stop here
  if (errors.some((e) => e.code === 'MISSING_REQUIRED_FIELD' && e.path === 'nodes')) {
    return { isValid: false, errors, warnings };
  }

  // Node parameter validation
  if (workflow.nodes && workflow.nodes.length > 0) {
    const nodeParamResult = validateAllNodeParameters(workflow.nodes);
    errors.push(...nodeParamResult.errors);
    warnings.push(...nodeParamResult.warnings);
  }

  // Edge validation
  if (workflow.edges && workflow.edges.length > 0) {
    const edgeResult = validateEdges(workflow.nodes, workflow.edges);
    errors.push(...edgeResult.errors);
    warnings.push(...edgeResult.warnings);

    // Validate merge nodes
    const mergeResult = validateMergeNodes(workflow.nodes, workflow.edges);
    warnings.push(...mergeResult.warnings);

    // Validate conditional branches
    const conditionalResult = validateConditionalBranches(workflow.nodes, workflow.edges);
    errors.push(...conditionalResult.errors);
    warnings.push(...conditionalResult.warnings);

    // Cycle detection
    const cycleErrors = detectCycles(workflow.nodes, workflow.edges);
    errors.push(...cycleErrors);
  }

  // Check for unknown node types
  if (workflow.nodes) {
    workflow.nodes.forEach((node, index) => {
      const nodeType = node.type || node.data?.nodeType;
      if (!nodeType) {
        // Already handled by structure validator
        return;
      }

      // Add warning for nodes that aren't explicitly configured
      if (node.data && !node.data.configured) {
        warnings.push({
          code: 'UNCONFIGURED_NODE',
          message: `Node "${node.data?.label || node.id}" is marked as not configured`,
          path: `nodes[${index}].data.configured`,
          nodeId: node.id,
          suggestion: 'Set "configured: true" if the node is ready for execution',
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate simplified format workflow JSON
 */
export function validateSimplifiedFormat(
  workflow: SimplifiedWorkflowJSON
): WorkflowJSONValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  // Validate name
  if (!workflow.name || workflow.name.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: 'Workflow name is required',
      path: 'name',
      suggestion: 'Add a "name" field with a descriptive name for the workflow',
    });
  }

  // Validate steps array
  if (!workflow.steps || !Array.isArray(workflow.steps)) {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: 'Steps array is required',
      path: 'steps',
      suggestion: 'Add a "steps" array with at least one step',
    });
    return { isValid: false, errors, warnings };
  }

  if (workflow.steps.length === 0) {
    errors.push({
      code: 'MISSING_TRIGGER',
      message: 'Workflow must have at least one step',
      path: 'steps',
      suggestion: 'Add at least one step (trigger) to the workflow',
    });
    return { isValid: false, errors, warnings };
  }

  // Validate steps
  validateSimplifiedSteps(workflow.steps, 'steps', errors, warnings);

  // Check first step is a trigger
  const firstStep = workflow.steps[0];
  if (firstStep && !isSimplifiedTriggerType(firstStep.type)) {
    warnings.push({
      code: 'FIRST_STEP_NOT_TRIGGER',
      message: `First step "${firstStep.type}" is not a trigger node`,
      path: 'steps[0].type',
      suggestion:
        'Consider starting with a trigger node (manualTrigger, contactCreated, webhookTrigger, etc.)',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Recursively validate simplified steps
 */
function validateSimplifiedSteps(
  steps: Array<{
    name?: string;
    type: string;
    config?: Record<string, unknown>;
    onTrue?: unknown[];
    onFalse?: unknown[];
    cases?: Array<{ value: string; steps: unknown[] }>;
    defaultCase?: unknown[];
  }>,
  basePath: string,
  errors: WorkflowJSONValidationError[],
  warnings: WorkflowJSONValidationWarning[]
): void {
  steps.forEach((step, index) => {
    const stepPath = `${basePath}[${index}]`;

    // Validate type
    if (!step.type || step.type.trim() === '') {
      errors.push({
        code: 'MISSING_REQUIRED_FIELD',
        message: `Step at ${stepPath} is missing required "type" field`,
        path: `${stepPath}.type`,
        suggestion: 'Add a "type" field specifying the step type',
      });
      return;
    }

    // Validate conditional branches recursively
    if (step.type === 'if' || step.type === 'condition' || step.type === 'conditionNode') {
      if (step.onTrue && Array.isArray(step.onTrue)) {
        validateSimplifiedSteps(
          step.onTrue as Array<{
            name?: string;
            type: string;
            config?: Record<string, unknown>;
          }>,
          `${stepPath}.onTrue`,
          errors,
          warnings
        );
      }
      if (step.onFalse && Array.isArray(step.onFalse)) {
        validateSimplifiedSteps(
          step.onFalse as Array<{
            name?: string;
            type: string;
            config?: Record<string, unknown>;
          }>,
          `${stepPath}.onFalse`,
          errors,
          warnings
        );
      }
    }

    // Validate switch cases recursively
    if (step.type === 'switch') {
      if (step.cases && Array.isArray(step.cases)) {
        step.cases.forEach((caseItem, caseIndex) => {
          if (caseItem.steps && Array.isArray(caseItem.steps)) {
            validateSimplifiedSteps(
              caseItem.steps as Array<{
                name?: string;
                type: string;
                config?: Record<string, unknown>;
              }>,
              `${stepPath}.cases[${caseIndex}].steps`,
              errors,
              warnings
            );
          }
        });
      }
      if (step.defaultCase && Array.isArray(step.defaultCase)) {
        validateSimplifiedSteps(
          step.defaultCase as Array<{
            name?: string;
            type: string;
            config?: Record<string, unknown>;
          }>,
          `${stepPath}.defaultCase`,
          errors,
          warnings
        );
      }
    }
  });
}

/**
 * Check if a type is a trigger type (for simplified format)
 */
function isSimplifiedTriggerType(type: string): boolean {
  const triggerTypes = [
    'manualTrigger',
    'scheduleTrigger',
    'webhookTrigger',
    'gmailTrigger',
    'googleSheetsTrigger',
    'accountCreated',
    'accountUpdated',
    'contactCreated',
    'contactUpdated',
    'accountCreatedNode',
    'accountUpdatedNode',
    'contactCreatedNode',
    'contactUpdatedNode',
  ];
  return triggerTypes.includes(type);
}

/**
 * Quick validation check (returns just valid/invalid without details)
 */
export function isValidWorkflowJSON(json: unknown): boolean {
  const result = validateWorkflowJSON(json);
  return result.isValid;
}

/**
 * Get validation summary for display
 */
export function getValidationSummary(result: WorkflowJSONValidationResult): string {
  if (result.isValid) {
    const warningCount = result.warnings.length;
    if (warningCount === 0) {
      return 'Workflow JSON is valid';
    }
    return `Workflow JSON is valid with ${warningCount} warning(s)`;
  }

  const errorCount = result.errors.length;
  const warningCount = result.warnings.length;

  let summary = `Validation failed: ${errorCount} error(s)`;
  if (warningCount > 0) {
    summary += `, ${warningCount} warning(s)`;
  }

  return summary;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(
  errors: WorkflowJSONValidationError[]
): string {
  return errors
    .map((error, index) => {
      let message = `${index + 1}. [${error.code}] ${error.message}`;
      if (error.path) {
        message += ` (at ${error.path})`;
      }
      if (error.suggestion) {
        message += `\n   Suggestion: ${error.suggestion}`;
      }
      return message;
    })
    .join('\n\n');
}

/**
 * Format validation warnings for display
 */
export function formatValidationWarnings(
  warnings: WorkflowJSONValidationWarning[]
): string {
  return warnings
    .map((warning, index) => {
      let message = `${index + 1}. [${warning.code}] ${warning.message}`;
      if (warning.path) {
        message += ` (at ${warning.path})`;
      }
      if (warning.suggestion) {
        message += `\n   Suggestion: ${warning.suggestion}`;
      }
      return message;
    })
    .join('\n\n');
}
